import { useMemo, type ReactNode } from "react";
import { Navigate, NavLink, useLocation, useParams } from "react-router";
import { applyFilter, enrich } from "@server/reporting/metrics";
import { useIncidentRows, useNoTicketRows } from "../../api/client";
import { ErrorBanner } from "../../components/ui";
import { WidePanel } from "../../components/Workspace";
import { FilterBar } from "./FilterBar";
import { ClosedTab, FeaturesTab, HeadlineTab, IncidentsTab, RaisedTab, SeverityTab, TicketsTab, TimeTab, TriageTab } from "./tabs";
import { useTicketReport } from "./useTicketReport";

const TABS = [
  { slug: "headline", title: "Headline stats" },
  { slug: "time", title: "Time to close" },
  { slug: "severity", title: "Severity" },
  { slug: "raised", title: "Tickets raised" },
  { slug: "features", title: "Features" },
  { slug: "closed", title: "Closed tickets" },
  { slug: "triage", title: "AI triage" },
  { slug: "tickets", title: "All tickets" },
  { slug: "incidents", title: "Incidents" },
] as const;

const formatTime = (iso: string): string =>
  new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

function Incidents() {
  const query = useIncidentRows();
  if (query.isPending) {
    return <p className="record-note">Loading…</p>;
  }
  if (query.isError) {
    return <ErrorBanner>We couldn't load the incidents.</ErrorBanner>;
  }
  return <IncidentsTab incidents={query.data} />;
}

export function Reports() {
  const { tab } = useParams();
  const location = useLocation();
  const { query, all, filter, rows, options, setFilter } = useTicketReport();
  const noTicketQuery = useNoTicketRows(tab === "triage");
  const noTicket = useMemo(() => applyFilter(enrich(noTicketQuery.data ?? []), filter), [noTicketQuery.data, filter]);

  if (!TABS.some((item) => item.slug === tab)) {
    return <Navigate to={{ pathname: "/reports/headline", search: location.search }} replace />;
  }

  const content = (): ReactNode => {
    const props = { rows, all };
    switch (tab) {
      case "time":
        return <TimeTab {...props} />;
      case "severity":
        return <SeverityTab {...props} />;
      case "raised":
        return <RaisedTab {...props} />;
      case "features":
        return <FeaturesTab {...props} />;
      case "closed":
        return <ClosedTab {...props} />;
      case "triage":
        if (noTicketQuery.isPending) {
          return <p className="record-note">Loading…</p>;
        }
        if (noTicketQuery.isError) {
          return <ErrorBanner>We couldn't load the submissions closed without a ticket.</ErrorBanner>;
        }
        return <TriageTab {...props} noTicket={noTicket} />;
      case "tickets":
        return <TicketsTab {...props} />;
      default:
        return <HeadlineTab {...props} />;
    }
  };

  return (
    <WidePanel>
      <div className="phase-head">
        <h1>Reports</h1>
        <p>
          Engineering tickets raised through Slack and Triage Desk since August 2025.
          {query.data && <> Updated {formatTime(query.data.generatedAt)}.</>}
          {query.data && (
            <>
              {" "}
              <button type="button" className="link-button" disabled={query.isFetching} onClick={() => void query.refetch()}>
                {query.isFetching ? "Refreshing…" : "Refresh"}
              </button>
            </>
          )}
        </p>
      </div>
      <nav className="report-tabs" aria-label="Report">
        {TABS.map((item) => (
          <NavLink key={item.slug} to={{ pathname: `/reports/${item.slug}`, search: location.search }} className={({ isActive }) => `report-tab${isActive ? " active" : ""}`}>
            {item.title}
          </NavLink>
        ))}
      </nav>
      {tab === "incidents" ? (
        <Incidents />
      ) : query.isPending ? (
        <p className="record-note">Loading…</p>
      ) : query.isError ? (
        <ErrorBanner>We couldn't load the reports. Refresh the page to try again.</ErrorBanner>
      ) : (
        <>
          <FilterBar filter={filter} options={options} onChange={setFilter} />
          <p className="record-note">
            Showing {rows.length.toLocaleString("en-GB")} of {all.length.toLocaleString("en-GB")} tickets.
          </p>
          {content()}
        </>
      )}
    </WidePanel>
  );
}
